import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react';

const InsideTextInput = createContext(false);
export const useTextInputFrame = () => useContext(InsideTextInput);

/** Keep the same DOM/editor selection while following the mobile visual viewport. */
export function TextInputFrame({ children, enabled = true }: { children: ReactNode; enabled?: boolean }) {
  const frame = useRef<HTMLDivElement>(null);
  const baseline = useRef(typeof window === 'undefined' ? 0 : window.innerHeight);
  const [active, setActive] = useState(false);
  const [dock, setDock] = useState<{ bottom: number; height: number; placeholder: number } | null>(null);
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport || !enabled) return;
    const update = () => {
      if (!active) { baseline.current = Math.max(baseline.current, window.innerHeight); setDock(null); return; }
      if ((window.innerWidth > 767 && !window.matchMedia('(pointer: coarse)').matches) || viewport.scale > 1.05 || baseline.current - viewport.height < 120) { setDock(null); return; }
      setDock(current => ({
        bottom: Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop),
        height: Math.max(80, viewport.height - 16),
        placeholder: current?.placeholder ?? frame.current?.offsetHeight ?? 0,
      }));
    };
    update();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    window.addEventListener('resize', update);
    return () => { viewport.removeEventListener('resize', update); viewport.removeEventListener('scroll', update); window.removeEventListener('resize', update); };
  }, [active, enabled]);
  return <InsideTextInput.Provider value={enabled}>
    {dock && <div aria-hidden style={{height:dock.placeholder}} />}
    <div ref={frame} data-text-input-docked={dock ? 'true' : undefined}
      className={dock ? 'fixed inset-x-2 z-[100] rounded-xl bg-white p-3 shadow-xl ring-1 ring-black/10 dark:bg-neutral-900 dark:ring-white/10' : 'min-w-0 w-full'}
      style={dock ? {bottom:dock.bottom, maxHeight:dock.height, overflowY:'auto', '--text-input-max-height': `${Math.max(40, dock.height - 132)}px`} as React.CSSProperties : undefined}
      onFocusCapture={event => { if (enabled && event.target instanceof HTMLTextAreaElement && !event.target.readOnly) setActive(true); }}
      onBlurCapture={event => { if (!event.currentTarget.contains(event.relatedTarget)) setActive(false); }}>
      {dock && <div className="mb-1 flex justify-end"><button type="button" className="px-2 py-1 text-sm text-neutral-500"
        onPointerDown={event => event.preventDefault()} onClick={() => { (document.activeElement as HTMLElement)?.blur(); setActive(false); }}>Done typing</button></div>}
      {children}
    </div>
  </InsideTextInput.Provider>;
}
